import { Controller } from "@hotwired/stimulus"

// Stimulus controller for the structured config form. Wires the cascading
// dropdowns (project → dataset → table → columns → distinct values),
// conditional fields (split.method swaps random vs temporal fields), and
// add/remove rows for models[] / regressors[].
//
// Each form field declares its YAML path via data-predict-config-form-path-param
// (set by the config_field helper). The connect() method indexes fields
// by path so cascade callbacks can find their downstream dependents
// without DOM walking.
export default class extends Controller {
  static targets = [
    "field",
    "modelsList", "regressorsList", "modelRow",
    "fallbackPairsList", "fallbackPairRow",
    "valueColumnStats",
    "splitRandomFields", "splitTemporalFields", "splitTimeline", "splitSegment", "splitBoundary", "splitRatio", "splitPartDays", "splitTooShort",
    "eventOrderStatus",
    "wildcardHint",
    "transactionalTableWrapper", "switchMlContractNote", "switchMlLegacyNote",
    "triggerHorizonStatus", "triggerHorizonCurve",
    "windowShort", "windowWorkable", "windowFact",
    "delayFact", "timingHeadline", "timingSum", "timingStacked",
  ]

  static values = { bqBaseUrl: String }

  // Path → element index for fast lookup of cascade dependents.
  connect() {
    this.fieldsByPath = {}
    for (const el of this.fieldTargets) {
      const p = el.dataset.predictConfigFormPathParam
      if (p) this.fieldsByPath[p] = el
    }
    this.bindCascade()
    this.refreshConditionals()
    this.showDelayFact()
    this.bootstrap()
  }

  // Wire change handlers for the cascade chain. Done programmatically
  // (rather than via data-action attributes) to keep the form template
  // free of Stimulus glue.
  bindCascade() {
    this.onChange("transactional.project",   () => this.cascadeDataset("transactional"))
    this.onChange("transactional.dataset",   () => this.transactionalDatasetChanged())
    this.onChange("transactional.table",     () => { this.cascadeColumns("transactional"); this.refreshConditionals() })
    this.onChange("behavioral.project",      () => this.cascadeDataset("behavioral"))
    this.onChange("behavioral.dataset",      () => this.cascadeTable("behavioral"))
    this.onChange("behavioral.table",        () => { this.cascadeColumns("behavioral"); this.refreshConditionals() })
    this.onChange("events.event_column",     () => this.cascadeEventValues({ refresh: true }))
    this.onChange("events.trigger_event",    () => this.triggerEventChanged())
    this.onChange("events.value_column",     () => this.refreshValueColumnStats())
    this.onChange("split.method",            () => this.refreshConditionals())
    this.onChange("behavioral.type",         () => this.refreshConditionals())
    this.onChange("transactional.type",      () => this.transactionalTypeChanged())
    for (const path of ["events.observation_window_days", "events.input_data_lag_days"]) {
      this.fieldsByPath[path]?.addEventListener("input", () => this.showDelayFact())
    }
    for (const path of ["events.trigger_event", "events.timestamp_granularity"]) {
      this.fieldsByPath[path]?.addEventListener("change", () => this.showDelayFact())
    }
  }

  // On first load, kick off datasets/tables fetches for any pre-filled
  // project/dataset values so the cascading dropdowns start populated
  // (rather than empty until the operator interacts).
  async bootstrap() {
    for (const root of ["transactional", "behavioral"]) {
      const proj = this.value(`${root}.project`)
      const ds   = this.value(`${root}.dataset`)
      const tbl  = this.value(`${root}.table`)
      if (proj) {
        await this.cascadeDataset(root, { keep: ds })
        if (ds) {
          await this.cascadeTable(root, { keep: tbl })
          await this.cascadeColumns(root)
        }
      }
    }
    const evCol = this.value("events.event_column")
    if (evCol) await this.cascadeEventValues()
    if (this.value("events.value_column")) await this.refreshValueColumnStats()
  }

  // ─── inference / hint helpers ──────────────────────────────────────

  // Click handler for the "Suggest order" button next to events.event_order.
  // Pulls the current project/dataset/table/event_column/user_column/timestamp_column
  // off the form and queries BQ for the inferred ordering. Pre-fills the
  // event_order text input with the comma-joined result.
  async suggestEventOrder(event) {
    event.preventDefault()
    const params = {
      ...this.transactionalSource(),
      event_column:     this.value("events.event_column"),
      user_column:      this.value("join.transactional_key"),
      timestamp_column: this.value("events.timestamp_column"),
    }
    const missing = this.missingParams(params)
    if (missing.length) {
      this.setStatus("eventOrderStatus", `Pick these first: ${missing.join(", ")}`, "error")
      return
    }
    this.setStatus("eventOrderStatus", "Inferring from data…", "loading")
    // Bypass the 1h server-side cache: a "Suggest" click is an explicit
    // recompute-from-current-data action, and tables can change between clicks
    // (e.g. during onboarding) — a stale cached result would mislead.
    params.refresh = 1
    const items = await this.fetchJson("/event_order", params)
    if (items.length === 0) {
      this.setStatus("eventOrderStatus", "No order returned (check BQ permissions or data)", "error")
      return
    }
    const el = this.fieldsByPath["events.event_order"]
    if (el) el.value = items.join(", ")
    this.setStatus("eventOrderStatus", `Suggested ${items.length} events from average first-occurrence order.`, "success")
  }

  // Click handler for the "Suggest horizon" button next to
  // events.trigger_horizon_days. Pre-fills the field with the horizon that
  // retains the most labelled positives and renders the curve behind that
  // choice; percentiles come back too, as context rather than as the answer.
  async suggestTriggerHorizon(event) {
    event.preventDefault()
    const params = {
      ...this.transactionalSource(),
      event_column:     this.value("events.event_column"),
      user_column:      this.value("join.transactional_key"),
      timestamp_column: this.value("events.timestamp_column"),
      trigger_event:    this.value("events.trigger_event"),
      predicted_event:  this.value("events.predicted_event"),
    }
    const missing = this.missingParams(params)
    // Sent after the required-field check so a blank optional field never reads
    // as "missing". The server resolves which column to pair on — that logic
    // belongs with the extract's rules, not here, and is testable there.
    params.label_match_key = this.value("join.label_match_key") || ""
    if (missing.length) {
      this.setStatus("triggerHorizonStatus", `Pick these first: ${missing.join(", ")}`, "error")
      return
    }
    this.setStatus("triggerHorizonStatus", "Estimating from data…", "loading")
    this.showWindowNote(null)

    // Bypass the 1h server-side cache — see suggestEventOrder: an explicit
    // "Suggest" click should reflect the table's current data, not a stale
    // estimate (tables change during onboarding).
    params.refresh = 1
    const url = new URL(`${this.bqBaseUrlValue}/trigger_horizon_estimate`, window.location.origin)
    for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v)
    let body
    try {
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })
      body = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        this.setStatus("triggerHorizonStatus", body.error || "(estimate unavailable)", "error")
        return
      }
    } catch (e) {
      this.setStatus("triggerHorizonStatus", `Fetch failed: ${e.message}`, "error")
      return
    }

    const s = body.stats || {}
    this.renderHorizonCurve(body.curve)
    if (!s.sample_size || s.peak_horizon == null) {
      this.setStatus("triggerHorizonStatus",
        "No trigger→outcome pairs in the data. Pick a smaller predicted event or seed more data.", "error")
      return
    }
    if (!body.reliable || body.suggestion == null) {
      // A handful of pairs is noise, not an estimate — and it usually
      // signals a deeper problem worth flagging here: the predicted
      // event barely ever follows the trigger for the same user column.
      // Name the column the estimate actually paired on: this message is
      // read mid-onboarding, and pointing at transactional_key when pairing
      // ran on label_match_key sends the operator to inspect the wrong one.
      const pairedOn = body.pairing_column || this.value("join.transactional_key")
      const advice = body.pairing_column_is_label_match_key
        ? `check that both events carry it`
        : `check that both events carry the join key (or set a label-match key column that appears on both)`
      this.setStatus("triggerHorizonStatus",
        `Only ${s.sample_size.toLocaleString()} trigger→outcome pair(s) — too few to estimate a window. ` +
        `This usually means the predicted event rarely follows the trigger for the same ` +
        `"${pairedOn}" value: ${advice}, or the outcome may be genuinely rare/new. ` +
        `Set the conversion window manually.`, "error")
      return
    }
    const el = this.fieldsByPath["events.trigger_horizon_days"]
    if (el) {
      el.value = body.suggestion
      // A script-set value fires no event; the timeline and Still-needed list redraw on input.
      el.dispatchEvent(new Event("input", { bubbles: true }))
    }
    const tail = " Longer labels a higher share of converters but censors more recent triggers out of" +
      " training; this is where the two cross. Adjust if you have a reason to."
    this.setStatus("triggerHorizonStatus",
      `Set to ${body.suggestion}d — keeps the most labelled conversions ` +
      `(${(s.peak_positives || 0).toLocaleString()} positives). ` +
      `Latency P50 ${s.p50}d · P90 ${s.p90}d over ${s.sample_size.toLocaleString()} converting triggers.${tail}`,
      "success")
    this.showWindowNote(s.p75)
  }

  // The observation window (Prediction step) and the input data lag (Data
  // source setup) add up, so an operator who typed 3 on one step can get scores
  // 5 days later. Restated with the real numbers under both fields and on Review.
  showDelayFact() {
    const windowDays = parseInt(this.value("events.observation_window_days"), 10) || 0
    const lag = parseInt(this.value("events.input_data_lag_days"), 10) || 0
    const total = windowDays + lag
    const days = (n) => `${n} day${n === 1 ? "" : "s"}`
    const parts = []
    if (windowDays > 0) parts.push(`${days(windowDays)} observation window`)
    if (lag > 0) parts.push(`${days(lag)} data lag`)
    const stacked = windowDays > 0 && lag > 0

    const delay = total > 0
      ? `Scores reach ad platforms ${days(total)} after each trigger${stacked ? ` (${parts.join(" + ")})` : ""}.`
      : "Scores reach ad platforms with no added delay."
    this.delayFactTargets.forEach(el => { el.textContent = delay })

    if (!this.hasTimingHeadlineTarget) return
    const trigger = this.value("events.trigger_event") || "trigger"
    this.timingHeadlineTarget.textContent = total > 0
      ? `Predicted ${days(total)} after each ${trigger}`
      : `Predicted at each ${trigger}, with no added delay`
    const sum = total > 0 ? `${trigger} + ${parts.join(" + ")} = ${days(total)} after the ${trigger}.` : ""
    const datesOnly = total > 0 && this.value("events.timestamp_granularity") === "day"
      ? " This data records dates only, so the days count from the end of the trigger day."
      : ""
    this.timingSumTarget.textContent = sum + datesOnly
    this.timingStackedTarget.hidden = !stacked
    this.timingStackedTarget.textContent = stacked
      ? `The observation window and the data lag add up: the window says ${days(windowDays)}, but scores land ${days(total)} after the ${trigger}.`
      : ""
  }

  // Short or workable from how long converters actually take, never from the
  // typed window: a score needs about a week to influence bidding.
  showWindowNote(p75) {
    const trigger = this.value("events.trigger_event")
    const predicted = this.value("events.predicted_event")
    this.windowFactTargets.forEach(el => {
      el.textContent = `75% of converters reach ${predicted} within ${p75} day${p75 === 1 ? "" : "s"} of ${trigger}.`
    })
    this.windowShortTarget.classList.toggle("hidden", p75 == null || p75 >= 7)
    this.windowWorkableTarget.classList.toggle("hidden", p75 == null || p75 < 7)
  }

  // The table behind the suggestion. Positives rise then FALL as the horizon
  // grows — the peak is the suggestion, and seeing both sides of it is the
  // difference between an operator trusting a number and understanding it.
  renderHorizonCurve(curve) {
    if (!this.hasTriggerHorizonCurveTarget) return
    const host = this.triggerHorizonCurveTarget
    if (!curve || !curve.length) { host.innerHTML = ""; return }

    const n = (v) => (v == null ? "—" : v.toLocaleString())
    const pct = (v) => (v == null ? "—" : `${v.toFixed(1)}%`)
    const rows = curve.map((c) => {
      const cls = c.peak
        ? "bg-sg-success-light font-medium text-sg-success-dark"
        : "text-sg-gray-600"
      return `<tr class="${cls}">
        <td class="px-2 py-1 text-right tabular-nums">${n(c.horizon)}d${c.peak ? " ←" : ""}</td>
        <td class="px-2 py-1 text-right tabular-nums">${n(c.positives)}</td>
        <td class="px-2 py-1 text-right tabular-nums">${pct(c.pct_captured)}</td>
        <td class="px-2 py-1 text-right tabular-nums">${n(c.retained)}</td>
        <td class="px-2 py-1 text-right tabular-nums">${n(c.censored)}</td>
        <td class="px-2 py-1 text-right tabular-nums">${c.positive_rate == null ? "—" : `${(c.positive_rate * 100).toFixed(2)}%`}</td>
      </tr>`
    }).join("")

    host.innerHTML = `
      <table class="w-full border-collapse text-xs">
        <caption class="mb-1 text-left text-xs text-sg-gray-500">
          Window trade-off. <strong>Positives kept</strong> is what the model actually trains on —
          it peaks (highlighted) because a longer window censors recent triggers out of training
          faster than it labels new conversions.
        </caption>
        <thead>
          <tr class="border-b border-sg-gray-300 text-sg-gray-500">
            <th class="px-2 py-1 text-right font-normal">Window</th>
            <th class="px-2 py-1 text-right font-normal">Positives kept</th>
            <th class="px-2 py-1 text-right font-normal">% converters labelled</th>
            <th class="px-2 py-1 text-right font-normal">Training rows</th>
            <th class="px-2 py-1 text-right font-normal">Censored</th>
            <th class="px-2 py-1 text-right font-normal">Base rate</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`
  }

  // Auto-fetched when events.value_column changes. Surfaces min/max/avg
  // next to value_clip_min so the operator picks a floor with context.
  async refreshValueColumnStats() {
    if (!this.hasValueColumnStatsTarget) return
    const params = { ...this.transactionalSource(), column: this.value("events.value_column") }
    if (this.missingParams(params).length) {
      this.valueColumnStatsTarget.textContent = ""
      return
    }
    this.valueColumnStatsTarget.textContent = "Fetching range…"
    const url = new URL(`${this.bqBaseUrlValue}/column_stats`, window.location.origin)
    for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v)
    try {
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })
      const body = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        this.valueColumnStatsTarget.textContent = body.error || "(stats unavailable)"
        return
      }
      const s = body.stats || {}
      const fmt = (n) => n == null ? "–" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })
      this.valueColumnStatsTarget.textContent = `Range in column: min ${fmt(s.min)} · avg ${fmt(s.avg)} · max ${fmt(s.max)} · ${fmt(s.non_null_count)} non-null rows`
    } catch (e) {
      this.valueColumnStatsTarget.textContent = `(stats fetch failed: ${e.message})`
    }
  }

  setStatus(targetName, message, kind) {
    const el = this[`${targetName}Target`]
    if (!el) return
    const colors = {
      loading: "text-sg-gray-500",
      success: "text-sg-success-dark",
      error:   "text-sg-error-dark",
    }
    el.className = `form-hint ${colors[kind] || "text-sg-gray-500"}`
    el.textContent = message
  }

  // ─── cascading fetches ──────────────────────────────────────────────

  async cascadeDataset(root, { keep = null } = {}) {
    const project = this.value(`${root}.project`)
    if (!project) return
    const items = await this.fetchJson("/datasets", { project })
    this.populateSelect(`${root}.dataset`, items.map(d => d.id), keep ?? this.value(`${root}.dataset`))
  }

  async cascadeTable(root, { keep = null } = {}) {
    const project = this.value(`${root}.project`)
    const dataset = this.value(`${root}.dataset`)
    if (!project || !dataset) return
    const items = await this.fetchJson("/tables", { project, dataset })
    // For GA4-style wildcard families (events_*), label them so the
    // operator sees what the entry means without having to know the
    // convention. The form persists the raw value (events_*); only
    // the visible label changes.
    const options = items.map(t => {
      if (t.type === "WILDCARD") {
        return { value: t.id, label: `${t.id}  (all daily shards)` }
      }
      return t.id
    })
    this.populateSelect(`${root}.table`, options, keep ?? this.value(`${root}.table`))
  }

  async cascadeColumns(root) {
    const source = root === "transactional" ? this.transactionalSource() : {
      project: this.value(`${root}.project`),
      dataset: this.value(`${root}.dataset`),
      table:   this.value(`${root}.table`),
    }
    if (this.missingParams(source).length) return

    const cols = await this.fetchJson("/columns", source)
    // Nested attributes (STRUCT leaves / JSON keys) arrive as dotted names
    // (e.g. conversion.type). Tag them "(nested)" so operators can tell them
    // apart from flat columns; flat names stay bare strings.
    const nest = name => name.includes(".") ? { value: name, label: `${name}  (nested)` } : name
    const rawNames = cols.map(c => c.name)
    const colNames = rawNames.map(nest)
    const topLevelNames = rawNames.filter(name => !name.includes("."))
    const numericCols = cols.filter(c => /INT|FLOAT|NUMERIC/.test(c.type)).map(c => nest(c.name))
    const stringCols = cols.filter(c => c.type === "STRING").map(c => nest(c.name))

    // Value column: numeric types, then STRING columns whose name reads like
    // money. The value isn't always a native number — Snowflake→BQ imports
    // land revenue as STRING, the same way they land timestamps — and the
    // feature builder casts this column to Float64 regardless, so a
    // numeric-only dropdown made those tables' value models unreachable while
    // the pipeline behind them would have run fine.
    //
    // The name heuristic is here for the same reason it's on the timestamp
    // dropdown below: unfiltered, every STRING column in the table qualifies,
    // and a list where NETWORK_USERID and EVENT_TYPE sit beside the revenue
    // column is worse than the numeric-only one it replaced. Numeric columns
    // stay unfiltered — a number is plausibly money whatever it's called.
    const looksLikeValue = c =>
      /(value|amount|revenue|price|profit|cost|total|spend|sales|gmv|ltv|payment|fee|margin)/i.test(c.name)
    const valueCols = [
      ...numericCols,
      ...cols.filter(c => c.type === "STRING" && looksLikeValue(c))
             .map(c => ({ value: c.name, label: `${c.name}  (STRING — cast to number)` })),
    ]

    // Timestamp dropdown: native types first, then epoch and STRING columns
    // whose name hints at a timestamp. The clock isn't always a native type —
    // GA4 stores event_timestamp as an INT64 epoch (micros), and Snowflake→BQ
    // imports land ISO-8601 timestamps as STRINGs. Predict::Extract::Timestamp
    // coerces both at extract (resolve_epoch for numeric, TIMESTAMP() for
    // string), so they must be offered too — omitting epoch columns made GA4's
    // real clock unselectable (eq-bank). The name heuristic
    // (_at|_ts|_date|_time|timestamp) keeps the dropdown short — random INT64s
    // like a count, or STRINGs like EMAIL/CITY, don't pollute it.
    const isTimestampType = c => /TIMESTAMP|DATETIME|DATE|TIME/.test(c.type)
    const isEpochType = c => /INT|FLOAT|NUMERIC/.test(c.type)
    const looksLikeTimestamp = c => /(_at|_ts|_date|_time|timestamp)$/i.test(c.name)
    const nativeTs = cols.filter(isTimestampType).map(c => nest(c.name))
    const epochTs = cols
      .filter(c => isEpochType(c) && looksLikeTimestamp(c))
      .map(c => ({ value: c.name, label: `${c.name}  (epoch — coerced at extract)` }))
    const stringTs = cols
      .filter(c => c.type === "STRING" && looksLikeTimestamp(c))
      .map(c => ({ value: c.name, label: `${c.name}  (STRING — coerced at extract)` }))
    const tsCols = [...nativeTs, ...epochTs, ...stringTs]

    if (root === "transactional") {
      this.populateSelect("join.transactional_key", colNames, this.value("join.transactional_key"))
      this.populateSelect("join.label_match_key",   colNames, this.value("join.label_match_key"))
      // Top-level only: the materializer compares this column against the predictions
      // table copy of it, and only top-level scalar columns are copied there.
      this.populateSelect("join.trigger_identity_key", topLevelNames, this.value("join.trigger_identity_key"))
      this.populateSelect("events.event_column",    colNames, this.value("events.event_column"))
      this.populateSelect("events.value_column",    valueCols, this.value("events.value_column"))
      this.populateSelect("events.product_column",  colNames, this.value("events.product_column"))
      this.populateSelect("events.timestamp_column", tsCols, this.value("events.timestamp_column"))
      this.populateSelect("transactional.unique_key", colNames)
      this.populateSelect("features.exclude_columns", colNames)
    } else if (root === "behavioral") {
      this.populateSelect("join.behavioral_key", colNames, this.value("join.behavioral_key"))
      // STRING-only: the per-event pivot (and its preflight check) require a
      // STRING event column, so don't offer non-STRING columns that would block.
      this.populateSelect("behavioral.event_column", stringCols, this.value("behavioral.event_column"))
      // Event-time column: timestamp-typed first (same ordering as the
      // transactional clock), since that's the per-trigger cutoff.
      this.populateSelect("behavioral.timestamp_column", tsCols, this.value("behavioral.timestamp_column"))
      this.populateSelect("behavioral.session_column", colNames, this.value("behavioral.session_column"))
    }

    // Cache columns per table so the repeatable fallback-pair rows (which
    // share a path and aren't in fieldsByPath) can be (re)populated by role.
    this.columnsByRoot = this.columnsByRoot || {}
    this.columnsByRoot[root] = rawNames
    this.populateFallbackSelects()
  }

  // Distinct event values back the trigger/predicted dropdowns. The server
  // caches the list for an hour (Gcp::BigQuery::DISTINCT_CACHE_TTL), so a
  // freshly-added event type — the data team rebuilds the source table and
  // introduces a new code — stays invisible behind the stale snapshot until
  // it expires. A DELIBERATE (re)selection of the event column passes
  // refresh:true to bust that cache and pull the live values; the initial
  // page-load bootstrap leaves refresh:false so opening a form never bills a
  // BigQuery scan. (Bootstrap's repopulation goes through fillSelect, whose
  // silent setSelected fires no change event — it never lands here with refresh.)
  async cascadeEventValues({ refresh = false } = {}) {
    const params = { ...this.transactionalSource(), column: this.value("events.event_column") }
    if (this.missingParams(params).length) return

    if (refresh) params.refresh = "1"
    this.eventValues = await this.fetchJson("/values", params)
    this.populateSelect("events.trigger_event", this.eventValues, this.value("events.trigger_event"))
    this.populatePredictedEvents(this.value("events.predicted_event"))
  }

  // The predicted event can't be the trigger. Only a deliberate trigger change
  // clears a predicted event that matches it; a saved config renders as saved.
  triggerEventChanged() {
    if (!this.eventValues) return
    const predicted = this.value("events.predicted_event")
    this.populatePredictedEvents(predicted === this.value("events.trigger_event") ? "" : predicted)
  }

  populatePredictedEvents(selected) {
    const trigger = this.value("events.trigger_event")
    this.populateSelect("events.predicted_event", (this.eventValues || []).filter(v => v !== trigger), selected)
  }

  // ─── conditionals ──────────────────────────────────────────────────

  refreshConditionals() {
    const method = this.value("split.method")
    if (this.hasSplitRandomFieldsTarget) this.splitRandomFieldsTarget.hidden   = method !== "random"
    if (this.hasSplitTemporalFieldsTarget) this.splitTemporalFieldsTarget.hidden = method !== "temporal"
    this.drawSplitTimeline()

    this.applySwitchMlContract()

    // Wildcard hint next to each table dropdown. There's one hint
    // per section (transactional, behavioral) and they're paired
    // visually with their corresponding select. We toggle by index:
    // the transactional hint pairs with the transactional table, etc.
    for (const hint of this.wildcardHintTargets) {
      const section = hint.closest("section")
      if (!section) continue
      const tableInput = section.querySelector("[data-predict-config-form-path-param$='.table']")
      hint.hidden = !(tableInput && tableInput.value.endsWith("*"))
    }
  }

  // Days in each part of the split, oldest to newest, over the Transactions Data
  // trigger window. The newest conversion window is still maturing, held out of
  // training under either method. A temporal cutoff follows
  // Predict::Config#effective_cutoff_date: end − conversion window − test days; a
  // random split instead shares every learnable day by test_ratio. Null when
  // there is nothing fixed to draw: an explicit cutoff date, or a window with no
  // set length.
  splitDays() {
    const random = this.value("split.method") === "random"
    if (!random && this.value("split.cutoff_date")) return null
    const lookback = Number(this.value("transactional.date_range.lookback_days"))
    const start = Date.parse(this.value("transactional.date_range.start"))
    const end = Date.parse(this.value("transactional.date_range.end"))
    const total = lookback || (end - start) / 86_400_000
    const maturing = Number(this.value("events.trigger_horizon_days"))
    if (!(total > 0) || !maturing) return null
    if (random) return { total, training: total - maturing, test: 0, maturing, ratio: Number(this.value("split.test_ratio")) }
    const test = Number(this.value("split.test_days")) || maturing
    return { total, training: total - maturing - test, test, maturing }
  }

  drawSplitTimeline() {
    if (!this.hasSplitTimelineTarget) return
    const days = this.splitDays()
    this.splitTimelineTarget.hidden = !days
    if (!days) return

    const { total, training, test, maturing, ratio } = days
    const random = ratio !== undefined
    this.splitBoundaryTarget.hidden = random
    this.splitRatioTarget.hidden = !random
    this.splitBoundaryTarget.max = total
    this.splitBoundaryTarget.value = training
    if (random) this.splitRatioTarget.value = Math.round(100 * ratio)
    this.splitSegmentTargets[0].style.setProperty("--test-px", `${random ? 10 * ratio : 0}px`)

    const span = n => `${n} day${n === 1 ? "" : "s"} (${Math.round(100 * n / total)}%)`
    const parts = [training, test, maturing]
    const readouts = random ? [`${Math.round(100 * (1 - ratio))}%`, `${Math.round(100 * ratio)}%`, span(maturing)]
      : parts.map(span)
    parts.forEach((n, i) => {
      this.splitSegmentTargets[i].style.width = `${100 * Math.max(n, 0) / total}%`
      this.splitPartDaysTargets[i].textContent = readouts[i]
    })
    this.splitTooShortTarget.hidden = training >= 1
  }

  // Moving the Training | Test boundary sets split.test_days, keeping a day on each side.
  dragSplit() {
    const { total, maturing } = this.splitDays()
    const training = Math.min(Math.max(Number(this.splitBoundaryTarget.value), 1), total - maturing - 1)
    this.fieldsByPath["split.test_days"].value = total - maturing - training
    this.drawSplitTimeline()
  }

  dragRatio() {
    this.fieldsByPath["split.test_ratio"].value = this.splitRatioTarget.value / 100
    this.drawSplitTimeline()
  }

  // Switch ML is a two-TABLE contract (`conversions` + `customers`), so there
  // is no single table to name — the extract joins them, and it decides that
  // from the ABSENCE of `transactional.table`.
  //
  // Two cases, deliberately different:
  //
  //   Operator PICKS the type   -> clear the table and take the contract. They
  //                                just said which shape their source is; making
  //                                them also know to blank a second field is the
  //                                confusion this whole control exists to remove.
  //   Existing config RENDERS   -> leave a table that is already saved. That is a
  //                                real configuration (yorkville reads a
  //                                hand-built view through this type), and
  //                                clearing it on page load would repoint a live
  //                                pipeline nobody asked to change. It shows the
  //                                legacy note instead, and blanking the table is
  //                                how you opt into the join.
  transactionalTypeChanged() {
    if (this.value("transactional.type") === "switch_ml") this.clearTransactionalTable()
    this.refreshConditionals()
    this.cascadeColumns("transactional")
  }

  // A contract dataset has no table to pick, so the dataset alone decides the
  // columns. A generic dataset waits for its table, whose change cascades them.
  async transactionalDatasetChanged() {
    await this.cascadeTable("transactional")
    if (this.usesSwitchMlContract()) await this.cascadeColumns("transactional")
  }

  usesSwitchMlContract() {
    return this.value("transactional.type") === "switch_ml" && !this.value("transactional.table")
  }

  // What names the transactional rowset to the BQ endpoints. The contract has
  // no table: the server reads `type` and resolves the joined rowset itself.
  transactionalSource() {
    const source = {
      project: this.value("transactional.project"),
      dataset: this.value("transactional.dataset"),
    }
    return this.usesSwitchMlContract()
      ? { ...source, type: "switch_ml" }
      : { ...source, table: this.value("transactional.table") }
  }

  missingParams(params) {
    return Object.entries(params).filter(([_, v]) => !v).map(([k]) => k)
  }

  // Silent clear, like fillSelect: a change event here would re-trigger the
  // column cascade against a table that is deliberately going away.
  clearTransactionalTable() {
    const el = this.fieldsByPath["transactional.table"]
    if (!el) return
    el.value = ""
    if (el.slimselect) this.quietly(() => el.slimselect.setSelected("", false))
  }

  // Hides AND DISABLES the field, never just hides. A hidden input still
  // submits, and a submitted table is precisely what opts the config out of
  // the join — the operator would pick "Switch ML", see no table field, and
  // silently get the single-table path anyway.
  applySwitchMlContract() {
    if (!this.hasTransactionalTableWrapperTarget) return

    const isSwitchMl = this.value("transactional.type") === "switch_ml"
    const table = this.value("transactional.table")
    const usesContract = this.usesSwitchMlContract()

    this.transactionalTableWrapperTarget.hidden = usesContract
    for (const el of this.transactionalTableWrapperTarget.querySelectorAll("input, select")) {
      el.disabled = usesContract
    }
    if (this.hasSwitchMlContractNoteTarget) this.switchMlContractNoteTarget.hidden = !usesContract
    if (this.hasSwitchMlLegacyNoteTarget) this.switchMlLegacyNoteTarget.hidden = !(isSwitchMl && table)
  }

  // ─── add/remove rows ───────────────────────────────────────────────

  // data-predict-config-form-kind-param names the list: "models" or "regressors".
  addModel(event) {
    event.preventDefault()
    const kind = event.params.kind
    this.addRow(this[`${kind}ListTarget`], kind)
  }

  addRow(list, kind) {
    const idx = list.querySelectorAll("[data-predict-config-form-target='modelRow']").length
    // Mirrors ModelArtifact::CLASSIFIER_TYPES / REGRESSOR_TYPES on the
    // server side. Keep these in sync — the server-rendered initial row
    // partial reads ModelArtifact directly.
    const types = kind === "regressors"
      ? ["xgboost_regressor", "lightgbm_regressor"]
      : ["xgboost", "lightgbm", "neural_net", "stacking", "catboost", "rumale_gbc"]

    // Built via DOM API rather than innerHTML so future extensions that
    // interpolate operator-controlled values (e.g. pre-fill a name)
    // can't introduce XSS.
    const inputClass = "form-input w-full"

    const wrapper = document.createElement("div")
    wrapper.className = "rounded border border-sg-gray-200 bg-sg-gray-50 p-4"
    wrapper.dataset.predictConfigFormTarget = "modelRow"
    wrapper.dataset.kind = kind

    const header = document.createElement("div")
    header.className = "flex items-start justify-between"
    const h3 = document.createElement("h3")
    h3.className = "text-sm font-semibold text-sg-gray-700"
    h3.textContent = `#${idx + 1}`
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "text-xs text-sg-error-dark hover:underline"
    removeBtn.dataset.action = "predict-config-form#removeRow"
    removeBtn.textContent = "Remove"
    header.append(h3, removeBtn)

    const grid = document.createElement("div")
    grid.className = "mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2"

    const nameWrap = document.createElement("div")
    const nameLabel = document.createElement("label")
    nameLabel.className = "form-label"
    nameLabel.textContent = "Name"
    const nameInput = document.createElement("input")
    nameInput.type = "text"
    nameInput.name = `config[${kind}][][name]`
    nameInput.className = inputClass
    nameWrap.append(nameLabel, nameInput)

    const typeWrap = document.createElement("div")
    const typeLabel = document.createElement("label")
    typeLabel.className = "form-label"
    typeLabel.textContent = "Type"
    const typeSelect = document.createElement("select")
    typeSelect.name = `config[${kind}][][type]`
    typeSelect.className = "w-full"
    typeSelect.dataset.controller = "search-select"
    typeSelect.dataset.searchSelectOptionsValue = JSON.stringify({ settings: { showSearch: false } })
    types.forEach(t => typeSelect.appendChild(this.buildOption(t, t)))
    typeWrap.append(typeLabel, typeSelect)

    grid.append(nameWrap, typeWrap)

    const paramsWrap = document.createElement("div")
    paramsWrap.className = "mt-3"
    const paramsLabel = document.createElement("label")
    paramsLabel.className = "form-label"
    paramsLabel.textContent = "Hyperparameters (JSON)"
    const paramsArea = document.createElement("textarea")
    paramsArea.name = `config[${kind}][][params]`
    paramsArea.rows = 4
    paramsArea.className = "mt-1 block w-full rounded border border-sg-gray-300 px-3 py-1.5 font-mono text-xs shadow-sm focus:border-sg-note-dark focus:outline-none focus:ring-1 focus:ring-sg-note-dark"
    paramsArea.placeholder = '{"n_estimators": 200, "max_depth": 6, "learning_rate": 0.1}'
    paramsArea.value = "{}"
    paramsWrap.append(paramsLabel, paramsArea)

    wrapper.append(header, grid, paramsWrap)
    list.appendChild(wrapper)
  }

  removeRow(event) {
    event.preventDefault()
    event.target.closest(
      "[data-predict-config-form-target='modelRow'], [data-predict-config-form-target='fallbackPairRow']"
    )?.remove()
  }

  // ─── helpers ───────────────────────────────────────────────────────

  onChange(path, fn) {
    const el = this.fieldsByPath[path]
    if (!el) return
    el.addEventListener("change", () => { if (!this.repopulating) fn() })
  }

  value(path) {
    const el = this.fieldsByPath[path]
    return el ? el.value : null
  }

  // Build the option list via the DOM API instead of innerHTML
  // concatenation. BQ values (distinct event names, dataset/table/column
  // identifiers) flow through here unsanitized — interpolating them
  // into an HTML string would let a malicious value like
  // `"><img src=x onerror=alert(1)>` execute. textContent + .value
  // setters don't parse HTML so the value is treated as text only.
  // Each entry in `options` can be either:
  //   - a string (used as both value and label), or
  //   - a {value, label} object (label diverges from value — used by
  //     cascadeColumns to render STRING-typed timestamp candidates
  //     with a "(STRING — coerced at extract)" suffix without changing
  //     the persisted value).
  populateSelect(path, options, selected) {
    this.fillSelect(this.fieldsByPath[path], options, selected)
  }

  // Element-based core of populateSelect — used directly for repeatable-row
  // selects (fallback pairs) that share a path and so aren't in fieldsByPath.
  fillSelect(el, options, selected) {
    if (!el || el.tagName !== "SELECT") return
    const kept = el.multiple
      ? Array.from(el.selectedOptions, o => o.value)
      : [String(selected ?? el.value)].filter(Boolean)
    el.replaceChildren()
    if (!el.multiple) el.appendChild(this.buildOption("", "— select —"))
    for (const v of options) {
      const { value, label } = typeof v === "object" && v !== null
        ? { value: v.value, label: v.label }
        : { value: v, label: v }
      el.appendChild(this.buildOption(value, label, kept.includes(String(value))))
    }
    const listed = Array.from(el.options, o => o.value)
    // A saved value the fetched list doesn't contain is still the config's
    // value — the table may have been renamed, a column dropped, or the
    // dropdown may simply not offer that type. Re-appending it keeps the
    // select honest; silently falling back to "— select —" looked like the
    // field was never set and wrote the erasure on the next save. Mirrors
    // what config_form_helper's render_input already does server-side. A
    // column list takes typed values by design, so those keep a bare label.
    for (const value of kept.filter(v => !listed.includes(v))) {
      el.appendChild(this.buildOption(value, el.multiple ? value : `${value}  (not in table)`, true))
    }
    // The host's search-select controller (SlimSelect) renders its own UI
    // from data captured at init — push the new options through its API.
    if (el.slimselect) {
      this.quietly(() => el.slimselect.setData(
        Array.from(el.options, o => ({ text: o.textContent, value: o.value, selected: o.selected }))
      ))
    }
  }

  // SlimSelect's setData and setSelected dispatch `change` on the select
  // whatever runAfterChange says. A repopulation would otherwise re-run the
  // cascade behind it, and a refilled event column would bust the /values
  // cache on every page load.
  quietly(fn) {
    this.repopulating = true
    try { fn() } finally { this.repopulating = false }
  }

  // Fill every fallback-pair row's column dropdowns from the cached column
  // lists. Selects are tagged by role (transactional/behavioral) rather than
  // path, since repeatable rows share a path. Preserves each row's value.
  populateFallbackSelects() {
    const byRoot = this.columnsByRoot || {}
    this.element.querySelectorAll("[data-fallback-role='transactional']")
      .forEach(el => this.fillSelect(el, byRoot.transactional || [], el.value))
    this.element.querySelectorAll("[data-fallback-role='behavioral']")
      .forEach(el => this.fillSelect(el, byRoot.behavioral || [], el.value))
  }

  addFallbackPair(event) {
    event.preventDefault()
    if (!this.hasFallbackPairsListTarget) return
    this.fallbackPairsListTarget.appendChild(this.buildFallbackRow())
    this.populateFallbackSelects()
  }

  buildFallbackRow() {
    const row = document.createElement("div")
    row.className = "flex items-end gap-2 rounded border border-sg-gray-200 bg-sg-gray-50 p-2"
    row.dataset.predictConfigFormTarget = "fallbackPairRow"

    const makeSide = (role, label) => {
      const wrap = document.createElement("div")
      wrap.className = "flex-1"
      const lab = document.createElement("label")
      lab.className = "form-label"
      lab.textContent = label
      const sel = document.createElement("select")
      sel.name = `config[join][fallback_pairs][][${role === "transactional" ? "transactional_key" : "behavioral_key"}]`
      sel.dataset.fallbackRole = role
      sel.className = "w-full"
      sel.dataset.controller = "search-select"
      sel.appendChild(this.buildOption("", "— select —"))
      wrap.append(lab, sel)
      return wrap
    }

    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "mb-1 shrink-0 text-xs text-sg-error-dark hover:underline"
    removeBtn.dataset.action = "predict-config-form#removeRow"
    removeBtn.textContent = "Remove"

    row.append(makeSide("transactional", "Transactions Data key"), makeSide("behavioral", "Analytics Data key"), removeBtn)
    return row
  }

  buildOption(value, label, selected = false) {
    const opt = document.createElement("option")
    opt.value = value
    opt.textContent = label
    if (selected) opt.selected = true
    return opt
  }

  async fetchJson(endpoint, params) {
    const url = new URL(`${this.bqBaseUrlValue}${endpoint}`, window.location.origin)
    Object.entries(params).forEach(([k, v]) => v != null && url.searchParams.set(k, v))
    try {
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })
      const body = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        // Surface the server-side error in the console + a brief inline
        // banner so operators don't stare at an empty dropdown wondering
        // whether the network failed, BQ is down, or they lack perms.
        const msg = body.error || `HTTP ${resp.status} from ${url.pathname}`
        console.error("[config-form]", msg, { url: url.toString(), body })
        this.showError(msg)
        return []
      }
      if (body.truncated) {
        // The server caps distinct-value lists; at the cap the list is
        // almost certainly incomplete. Say so rather than letting the
        // operator conclude an event type doesn't exist.
        this.showNotice(`The values list for ${params.column || "this column"} hit the server cap — ` +
                        `only the first ${ (body.items || []).length } (alphabetical) are shown.`)
      } else {
        // Don't let a stale truncation notice outlive the fetch that
        // triggered it (e.g. after switching to a smaller column).
        this.clearNotice()
      }
      return body.items || []
    } catch (e) {
      console.error("[config-form] fetch failed", e, { url: url.toString() })
      this.showError(`Network error fetching ${url.pathname}: ${e.message}`)
      return []
    }
  }

  showError(message) {
    let banner = document.getElementById("config-form-error-banner")
    if (!banner) {
      banner = document.createElement("div")
      banner.id = "config-form-error-banner"
      banner.className = "mb-4 rounded border border-sg-error-dark bg-sg-error-light px-4 py-2 text-sm text-sg-error-dark"
      this.element.prepend(banner)
    }
    banner.textContent = message
  }

  showNotice(message) {
    let banner = document.getElementById("config-form-notice-banner")
    if (!banner) {
      banner = document.createElement("div")
      banner.id = "config-form-notice-banner"
      banner.className = "mb-4 rounded border border-sg-alert-dark bg-sg-alert-light px-4 py-2 text-sm text-sg-alert-dark"
      this.element.prepend(banner)
    }
    banner.textContent = message
  }

  clearNotice() {
    document.getElementById("config-form-notice-banner")?.remove()
  }
}
